/**
 * Leitmotif MUI theme.
 *
 * Ported from landingpermit_app_v2 (palette + custom palette extension), then
 * layered with Leitmotif designer-brief overrides:
 *   - radii follow the radius roles in theme/tokens.css (control 4px by
 *     default; cards and papers take the container radius, chips the bit)
 *   - typography.fontFamily: Space Grotesk display / Inter UI / JetBrains Mono
 *   - components.MuiButton: no uppercase, no elevation
 *
 * Long-term plan: extract to a shared package consumed by both
 * connection-analyser and landingpermit. For now it lives per-repo.
 */

import { createTheme, type Theme } from '@mui/material/styles';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

declare module '@mui/material/styles' {
  interface PaletteOptions {
    custom?: {
      surfaceOverlay: string;
      tableHeaderBg: string;
      tableHeaderText: string;
      tableRowHover: string;
      inputFieldBg: string;
      chipBg: string;
      borderSubtle: string;
      borderDefault: string;
      borderMedium: string;
      textSubtle: string;
      appBarBg: string;
      navItemSelected: string;
      navItemHover: string;
    };
  }
  interface Palette {
    custom: PaletteOptions['custom'];
  }
}

const SPACE_GROTESK = "'Space Grotesk', 'Helvetica Neue', Arial, sans-serif";
const INTER = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const JETBRAINS_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

export type LeitmotifThemeMode = 'light' | 'dark';

export const createLeitmotifMuiTheme = (mode: LeitmotifThemeMode): Theme =>
  createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: { main: '#395C6B' },
            secondary: { main: '#5A7D8C' },
            background: { default: '#EEF4FC', paper: '#FFFFFF' },
            text: {
              primary: '#0A0A0A',
              secondary: 'rgba(10, 10, 10, 0.65)',
            },
            divider: 'rgba(10, 10, 10, 0.10)',
            custom: {
              surfaceOverlay: 'rgba(57, 92, 107, 0.04)',
              tableHeaderBg: 'rgba(57, 92, 107, 0.05)',
              tableHeaderText: '#395C6B',
              tableRowHover: 'rgba(57, 92, 107, 0.03)',
              inputFieldBg: 'rgba(57, 92, 107, 0.04)',
              chipBg: 'rgba(57, 92, 107, 0.10)',
              borderSubtle: 'rgba(10, 10, 10, 0.06)',
              borderDefault: 'rgba(10, 10, 10, 0.12)',
              borderMedium: 'rgba(10, 10, 10, 0.23)',
              textSubtle: 'rgba(10, 10, 10, 0.45)',
              appBarBg: 'rgba(57, 92, 107, 0.95)',
              navItemSelected: '#EEF4FC',
              navItemHover: 'rgba(128, 164, 237, 0.10)',
            },
          }
        : {
            primary: { main: '#5A8A9C' },
            secondary: { main: '#6B8A99' },
            background: { default: '#1F2A30', paper: '#283840' },
            text: {
              primary: '#E5E9F0',
              secondary: 'rgba(229, 233, 240, 0.75)',
            },
            divider: 'rgba(229, 233, 240, 0.10)',
            custom: {
              surfaceOverlay: 'rgba(40, 56, 64, 0.6)',
              tableHeaderBg: 'rgba(57, 92, 107, 0.25)',
              tableHeaderText: '#FFFFFF',
              tableRowHover: 'rgba(57, 92, 107, 0.10)',
              inputFieldBg: 'rgba(57, 92, 107, 0.15)',
              chipBg: 'rgba(57, 92, 107, 0.35)',
              borderSubtle: 'rgba(255, 255, 255, 0.08)',
              borderDefault: 'rgba(255, 255, 255, 0.15)',
              borderMedium: 'rgba(255, 255, 255, 0.23)',
              textSubtle: 'rgba(255, 255, 255, 0.45)',
              appBarBg: 'rgba(40, 56, 64, 0.95)',
              navItemSelected: 'rgba(57, 92, 107, 0.30)',
              navItemHover: 'rgba(107, 138, 153, 0.20)',
            },
          }),
    },

    shape: {
      // --radius-control; MUI needs a number here.
      borderRadius: 4,
    },

    typography: {
      fontFamily: INTER,
      h1: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      h2: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      h3: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      h4: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      h5: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      h6: { fontFamily: SPACE_GROTESK, fontWeight: 500, letterSpacing: '-0.01em' },
      overline: {
        fontFamily: INTER,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      body1: { fontFamily: INTER, fontSize: 14 },
      body2: { fontFamily: INTER, fontSize: 13 },
      button: { textTransform: 'none', fontWeight: 500 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: INTER,
          },
          code: { fontFamily: JETBRAINS_MONO },
        },
      },
      // Keyboard focus is shown by the accent ring in focus-overrides.css, so
      // MUI's pulsating focus ripple is off for every button-like control.
      MuiButton: {
        defaultProps: { disableFocusRipple: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiIconButton: { defaultProps: { disableFocusRipple: true } },
      MuiTab: { defaultProps: { disableFocusRipple: true } },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
            borderRadius: 'var(--radius-container)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)' },
          rounded: { borderRadius: 'var(--radius-container)' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 'var(--radius-control)' },
        },
      },
      // Canonical dropdown look (#632): every MUI Select renders the Leitmotif
      // thin chevron, matching the native `.lm-select` primitive and custom
      // triggers such as GeoFilterSelect.
      MuiSelect: {
        defaultProps: {
          IconComponent: KeyboardArrowDownRoundedIcon,
        },
        styleOverrides: {
          icon: {
            fontSize: '1.15rem',
            color: 'var(--fg-subtle, rgba(10, 10, 10, 0.45))',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 'var(--radius-bit)' },
        },
      },
    },
  });
